import type { BadgeProps } from "@panelgenie/shared-ui";
import type {
  InvocationKind,
  InvocationOutcome,
  InvocationPhoto,
  InvocationSuggestion,
  ParseResult,
} from "../types.js";
import { estimateCostUsd } from "./pricing.js";

/**
 * Pure presentation helpers for the support console. Kept free of React
 * so the formatting rules (outcome → badge colour, token/latency
 * rendering, raw-response stringification) are unit-tested directly.
 */

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

/**
 * Outcome → badge colour: success green, parse-failed amber, error red,
 * processing (an in-flight scan job) accent.
 */
export function outcomeBadgeVariant(outcome: InvocationOutcome): BadgeVariant {
  switch (outcome) {
    case "success":
      return "success";
    case "parse-failed":
      return "warning";
    case "provider-error":
      return "danger";
    case "processing":
      return "accent";
  }
}

/** Human label for an outcome. */
export function outcomeLabel(outcome: InvocationOutcome): string {
  switch (outcome) {
    case "success":
      return "Success";
    case "parse-failed":
      return "Parse failed";
    case "provider-error":
      return "Provider error";
    case "processing":
      return "Processing";
  }
}

/**
 * Human label for a photo's capture role (Phase 18a's `photo_metadata.role`).
 * Drives the badge on each row of the detail view's photo list. A photo with
 * no stored role (legacy / untagged capture) reads "Untagged". The wire role
 * may also be a value this console build predates, which falls through to
 * "Untagged" rather than crashing the row.
 */
export function photoRoleLabel(role: InvocationPhoto["role"]): string {
  switch (role) {
    case "nameplate":
      return "Nameplate";
    case "legendCard":
      return "Legend card";
    case "deadFrontOn":
      return "Cover-on";
    case "deadFrontOff":
      return "Cover-off";
    case "breakerCloseUp":
      return "Breaker close-up";
    case "busExposed":
      return "Bus";
    case "finding":
      return "Finding";
    case "other":
      return "Other";
    default:
      return "Untagged";
  }
}

/** Human label for a scan kind. */
export function kindLabel(kind: InvocationKind): string {
  switch (kind) {
    case "panel-scan":
      return "Panel scan";
    case "room-device-scan":
      return "Room device scan";
    case "nameplate-ocr":
      return "Nameplate OCR";
  }
}

/**
 * Human label for the Gemini HTTP backend that served a scan
 * (`ai_invocation.gemini_backend`): `"developer"` is the Gemini Developer API
 * (Google AI Studio, `?key=`), `"vertex"` is Vertex AI (Cloud). Returns `null`
 * for a null/empty value — non-Gemini providers (stub/replay) and rows recorded
 * before the column existed — so the caller can omit the "Backend" fact rather
 * than render a blank. An unknown non-empty value passes through verbatim so a
 * future backend isn't swallowed by a stale console build.
 */
export function geminiBackendLabel(backend: string | null): string | null {
  switch (backend) {
    case null:
    case "":
      return null;
    case "developer":
      return "Google AI Studio";
    case "vertex":
      return "Vertex AI";
    default:
      return backend;
  }
}

/**
 * KAN-168 — human label for the Gemini service tier that actually served a scan
 * (`ai_invocation.service_tier_served`). Returns `null` for null/empty (stub,
 * replay, provider-error, pre-column rows) so the caller omits the fact.
 * `mixed` means part of the scan's calls were downgraded from Priority to
 * Standard. Unknown values pass through verbatim.
 */
export function serviceTierLabel(tier: string | null): string | null {
  switch (tier) {
    case null:
    case "":
      return null;
    case "priority":
      return "Priority";
    case "standard":
      return "Standard";
    case "mixed":
      return "Mixed (partly downgraded)";
    default:
      return tier;
  }
}

/**
 * Render an ISO-8601 instant as a stable, timezone-independent
 * `YYYY-MM-DD HH:MM UTC`. We slice the ISO string rather than going
 * through `toLocaleString` so the output is deterministic across
 * machines and test runners (and unambiguous for support — every
 * record stamp is UTC). Non-ISO input is returned verbatim.
 */
export function formatInstant(iso: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  if (match === null) return iso;
  return `${match[1]} ${match[2]} UTC`;
}

/** Latency in ms, or an em dash when the provider never responded. */
export function formatLatency(latencyMs: number | null): string {
  if (latencyMs === null) return "—";
  if (latencyMs >= 1000) return `${(latencyMs / 1000).toFixed(2)} s`;
  return `${latencyMs} ms`;
}

/**
 * KAN-65 — the real-vs-backoff latency split for a row. `primary` is the
 * real provider latency (`latencyMs - backoffMs`) when backoff is known and
 * non-zero, else the plain total. `backoff` is the muted secondary
 * annotation (`+<t> backoff · <n>× retries`), present only when there was
 * measurable backoff; legacy rows (null `backoffMs`) and clean calls
 * (zero backoff) degrade to `primary` only, exactly like before.
 */
export type LatencyBreakdown = {
  readonly primary: string;
  readonly backoff: string | null;
};

export function latencyBreakdown(
  latencyMs: number | null,
  backoffMs: number | null,
  retryCount: number | null,
): LatencyBreakdown {
  if (latencyMs === null || backoffMs === null || backoffMs <= 0) {
    return { primary: formatLatency(latencyMs), backoff: null };
  }
  const realMs = Math.max(0, latencyMs - backoffMs);
  const retries = retryCount ?? 0;
  const retrySuffix =
    retries > 0 ? ` · ${retries}× ${retries === 1 ? "retry" : "retries"}` : "";
  return {
    primary: formatLatency(realMs),
    backoff: `+${formatLatency(backoffMs)} backoff${retrySuffix}`,
  };
}

/**
 * Token usage as `<in> in / <out> out`, or an em dash on a
 * provider-error (no response → no usage to count).
 */
export function formatTokens(
  usage: { readonly inputTokens: number; readonly outputTokens: number } | null
): string {
  if (usage === null) return "—";
  return `${usage.inputTokens} in / ${usage.outputTokens} out`;
}

/**
 * Estimated invocation cost as `~$<amount>`, or an em dash when it cannot
 * be priced (provider-error with no usage, or an unknown model — see
 * `estimateCostUsd`). The leading `~` marks it an estimate, not a billing
 * figure. Sub-cent costs get four decimals so a tiny nameplate scan does
 * not collapse to `~$0.00`; everything else shows cents.
 */
export function formatCost(
  usage: { readonly inputTokens: number; readonly outputTokens: number } | null,
  modelId: string
): string {
  const cost = estimateCostUsd(usage, modelId);
  if (cost === null) return "—";
  return cost < 0.01 ? `~$${cost.toFixed(4)}` : `~$${cost.toFixed(2)}`;
}

/**
 * Pretty-print the raw model response for the diagnostic <pre> block.
 * A string response (the common case — the model emits text) is shown
 * as-is; structured JSON is indented; absent responses (provider-error)
 * get an explicit placeholder.
 */
export function formatRawResponse(raw: unknown): string {
  if (raw === null || raw === undefined) return "(no response captured)";
  if (typeof raw === "string") return raw;
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
}

/**
 * The slice of the raw model response that pertains to one suggestion.
 * `scope` lets the UI label what it's showing: the single suggestion's
 * object, the whole document (fallback), or nothing captured.
 */
export type RawJsonSlice = {
  readonly text: string;
  readonly scope: "suggestion" | "document" | "none";
};

/**
 * Extract the part of the raw response that belongs to a single
 * suggestion. The stored raw response is the AI wire envelope
 * (`{ …, suggestions: [{ id, … }] }`, see `parseAiScanResponse`), so we
 * locate the element whose `id` matches and return it pretty-printed.
 * Falls back to the whole formatted document when the suggestion can't
 * be located — the "All photos" entry (`suggestionId === null`), a
 * parse-failed blob, or a non-JSON string response.
 */
export function suggestionRawJson(
  raw: unknown,
  suggestionId: string | null,
): RawJsonSlice {
  if (raw === null || raw === undefined) {
    return { text: "(no response captured)", scope: "none" };
  }
  const doc = typeof raw === "string" ? tryParseJson(raw) : raw;
  // A non-JSON string response (e.g. a model that emitted prose): show
  // it verbatim, no extraction possible.
  if (typeof doc === "string") {
    return { text: doc, scope: "document" };
  }
  if (suggestionId !== null && doc !== null && typeof doc === "object") {
    const suggestions = (doc as { readonly suggestions?: unknown }).suggestions;
    if (Array.isArray(suggestions)) {
      const match = suggestions.find(
        (s): s is Record<string, unknown> =>
          typeof s === "object" &&
          s !== null &&
          (s as { readonly id?: unknown }).id === suggestionId,
      );
      if (match !== undefined) {
        return { text: stringifyJson(match), scope: "suggestion" };
      }
    }
  }
  return { text: stringifyJson(doc), scope: "document" };
}

function tryParseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

function stringifyJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/** Confidence as a whole-percent string, e.g. `0.85` → `85%`. */
export function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Confidence → badge colour against the domain's 0.7 low-confidence
 * threshold: confident reads green, sub-threshold (flagged for review)
 * amber.
 */
export function confidenceBadgeVariant(
  confidence: number,
): NonNullable<BadgeProps["variant"]> {
  return confidence < 0.7 ? "warning" : "success";
}

/**
 * A one-line label for a suggestion's left-list row, derived defensively
 * from the parsed `proposedValue` (typed `unknown` over the wire). Reads
 * the per-kind discriminant and the most identifying fields; falls back
 * to the suggestion id when the shape is unrecognized so a row always
 * renders.
 */
export function suggestionSummary(suggestion: InvocationSuggestion): string {
  const value = suggestion.proposedValue;
  if (value === null || typeof value !== "object") return suggestion.id;
  const v = value as Record<string, unknown>;

  switch (v["kind"]) {
    case "panel-metadata": {
      const parts = [v["manufacturer"], v["model"]]
        .filter((p): p is string => typeof p === "string" && p.length > 0)
        .join(" ");
      const amp =
        typeof v["mainAmperage"] === "number" ? `${v["mainAmperage"]}A` : "";
      const detail = [parts, amp].filter(Boolean).join(" · ");
      return detail.length > 0 ? `Panel: ${detail}` : "Panel metadata";
    }
    case "breaker-read": {
      const pos = v["position"] as
        | { row?: unknown; column?: unknown }
        | undefined;
      const loc =
        pos !== undefined &&
        typeof pos.row === "number" &&
        typeof pos.column === "number"
          ? `R${pos.row}·C${pos.column}`
          : "breaker";
      const amp = typeof v["amperage"] === "number" ? `${v["amperage"]}A` : "";
      const pole = (v["pole"] as { kind?: unknown } | undefined)?.kind;
      const poleLabel = typeof pole === "string" ? pole : "";
      return `Breaker ${[loc, amp, poleLabel].filter(Boolean).join(" · ")}`;
    }
    case "device-read": {
      const name = typeof v["name"] === "string" ? v["name"] : "Device";
      const loc =
        typeof v["locationWithinRoom"] === "string"
          ? ` — ${v["locationWithinRoom"]}`
          : "";
      return `${name}${loc}`;
    }
    case "string":
    case "numeric": {
      const field = typeof v["field"] === "string" ? v["field"] : "field";
      const value =
        typeof v["value"] === "string" || typeof v["value"] === "number"
          ? String(v["value"])
          : "";
      return value.length > 0 ? `${field}: ${value}` : field;
    }
    default:
      return suggestion.id;
  }
}

/**
 * Build a Clerk dashboard deep-link for a user from the configured
 * template (which carries a `{userId}` placeholder). Returns `undefined`
 * when no template is configured, so callers render plain text. The id
 * is URL-encoded defensively, though Clerk ids are already URL-safe.
 */
export function clerkUserUrl(
  template: string | undefined,
  userId: string,
): string | undefined {
  if (template === undefined) return undefined;
  return template.replace("{userId}", encodeURIComponent(userId));
}

/** One-line summary of the recomputed parse outcome for table/summary use. */
export function parseSummary(parse: ParseResult): string {
  switch (parse.status) {
    case "ok":
      return `Parsed OK — ${parse.suggestionCount} suggestion${
        parse.suggestionCount === 1 ? "" : "s"
      }`;
    case "failed":
      return "Parse failed";
    case "no-response":
      return "No response to parse";
  }
}
